import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { safeHtml } from '../../../utils/sanitizeHtml';

const cleanText = (value) => typeof value === 'string' ? value.trim() : '';
const cleanList = (value) => Array.isArray(value) ? value.map(cleanText).filter(Boolean) : [];

export const parseFastGlobalMetadata = (value) => {
    if (value && typeof value === 'object') return value;
    if (typeof value !== 'string') return {};
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
        return {};
    }
};

const comparable = (value) => cleanText(value).replace(/\s+/g, ' ').toLocaleLowerCase();
const containsHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(value || '');

const buildSources = ({ metadata, quote, ref, page, targetBlockIds }) => {
    const locations = Array.isArray(metadata.evidence_locations)
        ? metadata.evidence_locations.filter((item) => item && typeof item === 'object')
        : [];
    const sources = locations.map((item, index) => ({
        key: `${item.original_source_id || item.doc_ref || 'source'}-${item.page || index}`,
        reference: cleanText(item.doc_ref) || cleanText(item.reference),
        page: item.page ?? null,
        quote: cleanText(item.quote),
        anchor: cleanText(item.original_source_id) || cleanText(item.target_block_id) || cleanText(item.doc_ref),
    }));

    if (sources.length === 0 && (cleanText(ref) || page || cleanText(quote))) {
        sources.push({
            key: 'card-source',
            reference: cleanText(ref),
            page: page ?? null,
            quote: cleanText(quote),
            anchor: cleanText(ref) || cleanList(targetBlockIds)[0] || '',
        });
    } else if (sources.length > 0 && cleanText(quote)) {
        const primaryIndex = sources.findIndex((source) => (
            (cleanText(ref) && source.reference === cleanText(ref)) || (page && source.page === page)
        ));
        const index = primaryIndex >= 0 ? primaryIndex : 0;
        if (!sources[index].quote) sources[index].quote = cleanText(quote);
    }

    return sources.filter((source) => source.reference || source.page || source.quote || source.anchor);
};

export const buildFastGlobalAnalysis = ({ metadataJson, quickView, impact, quote, ref, page, targetBlockIds }) => {
    const metadata = parseFastGlobalMetadata(metadataJson);
    const chain = metadata.causal_chain && typeof metadata.causal_chain === 'object'
        ? metadata.causal_chain
        : {};
    const trigger = cleanText(chain.trigger);
    const ruleSteps = cleanList(chain.rule_steps);
    const consequence = cleanText(chain.consequence);
    const summaryDescription = cleanText(quickView?.description);
    const businessExposure = cleanText(metadata.business_exposure);
    const fallbackImpact = cleanText(impact);
    const impactText = [businessExposure, fallbackImpact, summaryDescription]
        .find((value) => (
            value
            && comparable(value) !== comparable(summaryDescription)
            && comparable(value) !== comparable(consequence)
        )) || '';
    const recommendationRejected = metadata.recommendation_status === 'rejected';
    const nextStep = recommendationRejected ? '' : cleanText(quickView?.action);
    const quantification = metadata.optional_quantification && typeof metadata.optional_quantification === 'object'
        ? metadata.optional_quantification
        : null;
    const quantificationValid = Boolean(
        quantification
        && metadata.quantification_status === 'validated'
        && quantification.result !== null
        && quantification.result !== undefined
        && cleanText(quantification.unit)
        && cleanText(quantification.kind)
        && cleanList(quantification.assumptions).length > 0
    );

    return {
        metadata,
        causalChain: (trigger || ruleSteps.length || consequence) ? { trigger, ruleSteps, consequence } : null,
        impactText,
        aggravations: cleanList(metadata.aggravations),
        protections: cleanList(metadata.protections_or_limits),
        uncertainties: cleanList(metadata.uncertainties),
        nextStep,
        recommendationRejected,
        quantification: quantificationValid ? {
            result: quantification.result,
            unit: cleanText(quantification.unit),
            kind: cleanText(quantification.kind),
            assumptions: cleanList(quantification.assumptions),
        } : null,
        sources: buildSources({ metadata, quote, ref, page, targetBlockIds }),
    };
};

export const hasUsefulFastGlobalAnalysis = (analysis) => Boolean(
    analysis.causalChain || analysis.impactText || analysis.aggravations.length
    || analysis.protections.length || analysis.uncertainties.length || analysis.nextStep
    || analysis.quantification
);

const TextList = ({ items }) => (
    <ul className="fast-global-analysis__list">
        {items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
    </ul>
);

const Section = ({ title, children, className = '' }) => (
    <section className={`fast-global-analysis__section ${className}`.trim()}>
        <h4>{title}</h4>
        {children}
    </section>
);

const FastGlobalAnalysisPanel = ({ id, analysis, detail = null, onRetry = null }) => {
    const { t } = useTranslation('review');
    const kindLabels = useMemo(() => ({
        exact: t('ai_comment_card.analysis.quantificationKinds.exact'),
        upper_bound: t('ai_comment_card.analysis.quantificationKinds.upperBound'),
        lower_bound: t('ai_comment_card.analysis.quantificationKinds.lowerBound'),
        scenario: t('ai_comment_card.analysis.quantificationKinds.scenario'),
    }), [t]);
    const isStreamView = Boolean(detail);
    const errorMessageKey = detail?.errorCode
        ? `ai_comment_card.analysis.errors.${detail.errorCode}`
        : 'ai_comment_card.analysis.error';
    return (
        <div id={id} className="fast-global-analysis" role="region" aria-label={t('ai_comment_card.analysis.panelLabel')}>
            {!isStreamView && analysis.causalChain && (
                <Section title={t('ai_comment_card.analysis.how')} className="fast-global-analysis__section--chain">
                    <ol className="fast-global-analysis__chain">
                        {analysis.causalChain.trigger && <li><span>{t('ai_comment_card.analysis.trigger')}</span><p>{analysis.causalChain.trigger}</p></li>}
                        {analysis.causalChain.ruleSteps.length > 0 && <li><span>{t('ai_comment_card.analysis.rules')}</span><TextList items={analysis.causalChain.ruleSteps} /></li>}
                        {analysis.causalChain.consequence && <li><span>{t('ai_comment_card.analysis.consequence')}</span><p>{analysis.causalChain.consequence}</p></li>}
                    </ol>
                </Section>
            )}
            {!isStreamView && analysis.impactText && <Section title={t('ai_comment_card.analysis.impact')}><p>{analysis.impactText}</p></Section>}
            {!isStreamView && analysis.aggravations.length > 0 && <Section title={t('ai_comment_card.analysis.aggravations')}><TextList items={analysis.aggravations} /></Section>}
            {!isStreamView && analysis.protections.length > 0 && <Section title={t('ai_comment_card.analysis.protections')}><TextList items={analysis.protections} /></Section>}
            {!isStreamView && analysis.uncertainties.length > 0 && <Section title={t('ai_comment_card.analysis.uncertainties')} className="fast-global-analysis__section--confirm"><TextList items={analysis.uncertainties} /></Section>}
            {!isStreamView && analysis.nextStep && <Section title={t('ai_comment_card.analysis.nextStep')}><p>{analysis.nextStep}</p></Section>}
            {!isStreamView && analysis.quantification && (
                <Section title={t('ai_comment_card.analysis.quantification')}>
                    <div className="fast-global-analysis__estimate"><strong>{analysis.quantification.result} {analysis.quantification.unit}</strong><span>{kindLabels[analysis.quantification.kind] || analysis.quantification.kind}</span></div>
                    <span className="fast-global-analysis__subheading">{t('ai_comment_card.analysis.assumptions')}</span>
                    <TextList items={analysis.quantification.assumptions} />
                    <p className="fast-global-analysis__note">{t('ai_comment_card.analysis.quantificationNote')}</p>
                </Section>
            )}
            {detail?.sections?.plain_language_summary && <Section title={t('ai_comment_card.analysis.summary')}><p>{detail.sections.plain_language_summary}</p></Section>}
            {detail?.sections?.contractual_mechanism && <Section title={t('ai_comment_card.analysis.mechanism')}><p>{detail.sections.contractual_mechanism}</p></Section>}
            {detail?.sections?.business_implications?.length > 0 && <Section title={t('ai_comment_card.analysis.businessImplications')}><TextList items={detail.sections.business_implications} /></Section>}
            {detail?.sections?.exposure_increasers?.length > 0 && <Section title={t('ai_comment_card.analysis.aggravations')}><TextList items={detail.sections.exposure_increasers} /></Section>}
            {detail?.sections?.exposure_limits?.length > 0 && <Section title={t('ai_comment_card.analysis.protections')}><TextList items={detail.sections.exposure_limits} /></Section>}
            {detail?.sections?.points_to_confirm?.length > 0 && <Section title={t('ai_comment_card.analysis.uncertainties')}><TextList items={detail.sections.points_to_confirm} /></Section>}
            {detail?.content && (
                containsHtml(detail.content) ? (
                    <div
                        className="fast-global-analysis__stream-content ai-memo-prose"
                        dangerouslySetInnerHTML={safeHtml(detail.content)}
                    />
                ) : (
                    <div className="fast-global-analysis__stream-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.content}</ReactMarkdown>
                    </div>
                )
            )}
            {detail?.status === 'running' && (
                <p className="fast-global-analysis__progress" role="status">
                    <span className="fast-global-analysis__progress-dot" aria-hidden="true" />
                    {t('ai_comment_card.analysis.loading')}
                </p>
            )}
            {detail?.status === 'waiting' && (
                <div className="fast-global-analysis__waiting" role="status">
                    <span>{t('ai_comment_card.analysis.inProgress')}</span>
                    {onRetry && <button type="button" onClick={onRetry}>{t('ai_comment_card.analysis.checkAgain')}</button>}
                </div>
            )}
            {detail?.status === 'error' && (
                <div className="fast-global-analysis__error" role="alert">
                    <span>{t(errorMessageKey, { defaultValue: t('ai_comment_card.analysis.error') })}</span>
                    {detail.requestId && (
                        <small>{t('ai_comment_card.analysis.errorReference', { id: detail.requestId })}</small>
                    )}
                    {onRetry && detail.retryable !== false && (
                        <button type="button" onClick={onRetry}>{t('ai_comment_card.analysis.retry')}</button>
                    )}
                </div>
            )}
        </div>
    );
};

export default FastGlobalAnalysisPanel;
